// cSpell:ignore aandeel Eigenaar gebouw kost oppervlakte panden Scenariolaan Verdeling verschilt volgens
// What each owner owns of a building (Lewis, 2026-10-19: "how do we know
// what each owner owns?"): the units they own (each unit's owner, from its
// property members: ownerKeyByUnit), weighed by the key that split the
// owners' part, as the engine weighs them. "55/85 van het gebouw (Verdeling
// volgens oppervlakte)", "4/7" by the equal key.
//
// The key of a category split by key is that key: the owners' part is the
// vacancy and the fixed share of its units. A reading's or a metering
// company's category names the key that split its owners' part
// (ownerPartKey). A unit's own cost has no key: it is all its owner's.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { IArchivableUnit, keySplitsForCosts } from "./groupUnits.utils";
import { keyDisplayName } from "./keyName.utils";
import { formatWeight } from "./numberInput.utils";
import { ApportionmentKey } from "./types";

type TKey = Pick<ApportionmentKey, "id" | "name" | "isEqual" | "weights">;
type TUnit = IArchivableUnit & { id: string };

// The units a building's key splits over, as a settlement's figures name
// them (see keySplitsForCosts): `units`, else every unit of the group
// (`allUnits`) or, on older snapshots, their number.
export interface IBuildingUnits {
  units: TUnit[];
  allUnits?: TUnit[];
  unitCount?: number;
}

// "55/85".
export const formatShare = (weight: number, total: number): string =>
  `${formatWeight(weight)}/${formatWeight(total)}`;

// The keys a settlement split the owners' part of its building's costs by,
// each once, in the order of its categories: a category's key, or the key
// of a reading's or a metering company's owners' part.
export const ownerPartKeysOf = (
  byCat: Record<string, { key?: TKey; ownerPartKey?: TKey }>,
): TKey[] => {
  const keys: TKey[] = [];
  for (const split of Object.values(byCat)) {
    const keyDef = split.key ?? split.ownerPartKey;
    if (keyDef && !keys.some(known => known.id === keyDef.id)) {
      keys.push(keyDef);
    }
  }
  return keys;
};

// How one key weighs the building's units over a period: each unit's
// weight and their total (one split, see keySplitsForCosts).
const keySplitOver = (params: {
  keyDef: TKey;
  building: IBuildingUnits;
  period?: { from: string; to: string };
}) =>
  keySplitsForCosts({
    keyDef: params.keyDef,
    units: params.building.units,
    periods: params.period ? [params.period] : undefined,
    allUnits: params.building.allUnits,
    unitCount: params.building.unitCount,
  })[0];

export interface IOwnerKeyShare {
  keyId: string;
  // The key's name in sentence case (keyDisplayName).
  keyName: string;
  weight: number;
  total: number;
  // Each of the owner's units with its weight, in the owner's order.
  units: { unitId: string; weight: number }[];
}

// What one owner's units weigh of the building by each key that split the
// owners' part.
export const ownerKeyShares = (params: {
  keys: TKey[];
  building: IBuildingUnits;
  ownerUnitIds: string[];
  period?: { from: string; to: string };
}): IOwnerKeyShare[] =>
  params.keys.flatMap(keyDef => {
    const split = keySplitOver({ ...params, keyDef });
    if (!split || split.total <= 0) return [];
    const units = params.ownerUnitIds.map(unitId => ({
      unitId,
      weight: split.weightOf(unitId),
    }));
    return [
      {
        keyId: keyDef.id,
        keyName: keyDisplayName(keyDef.name),
        weight: units.reduce((sum, unit) => sum + unit.weight, 0),
        total: split.total,
        units,
      },
    ];
  });

// Every owner's share of the building by one key, the largest first:
// "Els Eigenaar 55/85 · Mr. Monopoly 30/85" (the distribution popup). Units
// without a known owner share the owner key "".
export const ownersShareByKey = (params: {
  keyDef: TKey;
  building: IBuildingUnits;
  ownerKeyByUnit: Record<string, string>;
  period?: { from: string; to: string };
}): { ownerKey: string; weight: number; total: number }[] => {
  const split = keySplitOver(params);
  if (!split || split.total <= 0) return [];
  const byOwner: Record<string, number> = {};
  for (const unit of split.units) {
    const ownerKey = params.ownerKeyByUnit[unit.id] ?? "";
    byOwner[ownerKey] = (byOwner[ownerKey] ?? 0) + split.weightOf(unit.id);
  }
  return Object.entries(byOwner)
    .map(([ownerKey, weight]) => ({ ownerKey, weight, total: split.total }))
    .sort(
      (a, b) => b.weight - a.weight || a.ownerKey.localeCompare(b.ownerKey),
    );
};

const byAddress = (a: string, b: string): number =>
  a.localeCompare(b, "nl", { numeric: true, sensitivity: "base" });

// The line under an owner's card and its "i" (Lewis, 2026-10-19):
// - an owner of several units: "Eigenaar van 4 panden · 55/85 van het
//   gebouw (Verdeling volgens oppervlakte)", each unit in the "i" by its
//   full address and its weight ("Scenariolaan 1 bus 1, 9000 Gent ·
//   10/85");
// - an owner of one unit (its address is the card's title): only the
//   share;
// - keys that differ per category: "aandeel verschilt per kost", the share
//   by each key in the "i", each followed by its units' weights.
// No share for an owner of the whole building, nor without a key (unit
// costs only): the units alone.
export const ownerCardNote = (params: {
  units: { unitId: string; address: string }[];
  shares: IOwnerKeyShare[];
}): { subtitle?: string; lines: string[] } => {
  const { units } = params;
  const shares = params.shares.filter(share => share.weight < share.total);
  const isSeveral = units.length > 1;
  const addressOf = (unitId: string) =>
    units.find(unit => unit.unitId === unitId)?.address ?? unitId;
  const unitLines = (share?: IOwnerKeyShare): string[] =>
    [...units]
      .sort((a, b) => byAddress(a.address, b.address))
      .map(unit => {
        const weight = share?.units.find(u => u.unitId === unit.unitId);
        return share && weight
          ? `${addressOf(unit.unitId)} · ${formatShare(
              weight.weight,
              share.total,
            )}`
          : unit.address;
      });
  const count = isSeveral
    ? getLocalizedText("settlement.summary.card.owner_of_count", {
        count: String(units.length),
      })
    : undefined;
  if (shares.length === 0) {
    return { subtitle: count, lines: isSeveral ? unitLines() : [] };
  }
  if (shares.length === 1) {
    const [share] = shares;
    const sharePart = getLocalizedText("settlement.summary.card.owner_share", {
      share: formatShare(share.weight, share.total),
      key: share.keyName,
    });
    return {
      subtitle: [count, sharePart].filter(Boolean).join(" · "),
      lines: isSeveral ? unitLines(share) : [],
    };
  }
  return {
    subtitle: [
      count,
      getLocalizedText("settlement.summary.card.owner_share_varies"),
    ]
      .filter(Boolean)
      .join(" · "),
    lines: shares.flatMap(share => [
      `${share.keyName}: ${formatShare(share.weight, share.total)}`,
      ...(isSeveral ? unitLines(share) : []),
    ]),
  };
};
